import _ from 'lodash'
import { fromJS } from 'immutable'
import { convertToRaw, convertFromRaw, ContentState } from 'draft-js'
import linkifyEditorState from './linkifyEditorState'

export const mentionMutability = 'IMMUTABLE'

const kanboTypesMap = {
  'unordered-list-item' : 'UNORDERED_ITEM',
  'ordered-list-item'   : 'ORDERED_ITEM',
  'unchecked-list-item' : 'UNCHECKED_ITEM',
  'checked-list-item'   : 'CHECKED_ITEM',
  'blockquote'          : 'QUOTE',
  'header-one'          : 'HEADER_1',
  'header-two'          : 'HEADER_2',
  'header-three'        : 'HEADER_3',
  'header-four'         : 'HEADER_4',
  'header-five'         : 'HEADER_5',
  'header-six'          : 'HEADER_6',
  'BOLD'                : 'BOLD',
  'ITALIC'              : 'ITALIC',
  'UNDERLINE'           : 'UNDERLINE',
  'mention'             : 'MENTION',
  'QUESTION'            : 'QUESTION',
  'LINK'                : 'LINK'
}

const convertEntity = (entityRange, entity) => {
  let ent = {
    Type: kanboTypesMap[entity.type] || entity.type,
    Offset: entityRange.offset,
    Length: entityRange.length
  }

  switch (entity.type) {
    case 'mention':
      return {
        ...ent,
        Data: {
          UserId: entity.data.mention.get('id')
        }
      }
    case 'LINK':
      return {
        ...ent,
        Data: {
          Url: entity.data.url
        }
      }
    default:
      return ent
  }
}

const convertBlock = (rawBlock, rawState, {
  textOnly = false
} = {}) => {
  let Block = {
    Text: rawBlock.text
  }

  if (!textOnly) {
    const Type = kanboTypesMap[rawBlock.type] || rawBlock.type
    if (Type !== 'unstyled') {
      Block.Type = Type
    }

    const Ranges = [
      ...rawBlock.inlineStyleRanges.map(inlineStyleRange => ({
        Type: kanboTypesMap[inlineStyleRange.style] || inlineStyleRange.style,
        Offset: inlineStyleRange.offset,
        Length: inlineStyleRange.length
      })),
      ...rawBlock.entityRanges.map(entityRange => convertEntity(entityRange, rawState.entityMap[entityRange.key]))
    ]
    if (Ranges.length > 0) {
      Block.Ranges = Ranges
    }

    switch (Type) {
      case 'UNORDERED_ITEM':
      case 'ORDERED_ITEM':
        Block.Data = {
          Depth: rawBlock.depth
        }
        break
      default:
    }
  }

  return Block
}

export const convertEditorStateToKanBoFormat = (editorState, {
  textOnly = false
} = {}) => {
  const rawState = convertToRaw(linkifyEditorState(editorState).getCurrentContent())

  return {
    Blocks: rawState.blocks.map(rawBlock => convertBlock(rawBlock, rawState, {
      textOnly
    })),
    v: 2
  }
}

const rawTypesMap = _.invert(kanboTypesMap)

const convertKanBoRangeToRaw = (Range, Block) => {
  const type = rawTypesMap[Range.Type] || Range.Type

  let inlineStyleRange = null
  let entity = null
  let entityRange = null

  switch (Range.Type) {
    case 'BOLD':
    case 'ITALIC':
    case 'UNDERLINE':
      inlineStyleRange = {
        offset: Range.Offset,
        length: Range.Length,
        style: type
      }
      break
    case 'MENTION':
    case 'QUESTION':
      entity = {
        type,
        mutability: mentionMutability,
        data: {
          mention: fromJS({
            id: Range.Data.UserId,
            name: Block.Text.substr(Range.Offset, Range.Length)
          })
        }
      }
      break
    case 'LINK':
      entity = {
        type,
        mutability: mentionMutability,
        data: {
          url: Range.Data.Url
        }
      }
      break
    default:
      entity = {
        type
      }
      break
  }

  if (entity) {
    entityRange = {
      offset: Range.Offset,
      length: Range.Length
    }
  }

  return {
    inlineStyleRange,
    entity,
    entityRange
  }
}

export const convertKanBoFormatToContentState = (kanboFormat, {
  textOnly = false
} = {}) => {
  let entityId = 0

  let entityMap = {}
  let blocks = []

  if (kanboFormat && kanboFormat['Blocks']) {
    blocks = kanboFormat['Blocks'].map(KanboBlock => {
      let block = {
        text: KanboBlock.Text || '',
        type: (!textOnly && KanboBlock.Type) ? rawTypesMap[KanboBlock.Type] : 'unstyled',
        inlineStyleRanges: [],
        entityRanges: []
      }

      if (!textOnly) {
        KanboBlock.Ranges && KanboBlock.Ranges.forEach(Range => {
          const { inlineStyleRange, entity, entityRange } = convertKanBoRangeToRaw(Range, KanboBlock)

          if (inlineStyleRange) {
            block.inlineStyleRanges.push(inlineStyleRange)
          } else if (entity) {
            entityMap[entityId.toString()] = entity

            block.entityRanges.push({
              ...entityRange,
              key: entityId.toString()
            })

            entityId++
          }
        })

        switch (KanboBlock.Type) {
          case 'UNORDERED_ITEM':
          case 'ORDERED_ITEM':
            block.depth = KanboBlock.Data.Depth
            break
          default:
        }
      }

      return block
    })
  }

  if (blocks.length > 0) {
    return convertFromRaw({
      entityMap,
      blocks
    })
  }

  return ContentState.createFromText('')
}
